import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import Select from "react-select";
import { API_URL } from "../../../../constants/index";
import {
  GetServiceCaller,
  PostFormServiceCaller,
} from "../../../../services/serviceCaller";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";

const AddTrainingIncentive = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const today = moment().format("DD-MMM-YYYY");
  const initialFormData = {
    ProjectID: "",
    FromDate: today,
    ToDate: today,
    Days: 1,
    ClientID: "",
    Client: "",
    TrainigTitle: "",
    IsIncludeHoliday: 0,
    AmountPerDay: "",
    AppliedPerDayAmount: "",
    TotalAmount: 1000,
    TotatAmountApplied: "",
    TrainingType: 2016,
    Remark: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [projectOption, setProjectOption] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeOption, setTypeOption] = useState([]);
  const [disableDates, setDisableDates] = useState([]);
  const [errors, setErrors] = useState({});

  // Validation schema - Removed TrainigTitle validation
  const validationSchema = Yup.object().shape({
    ProjectID: Yup.string().required("Project is required"),
    TrainingType: Yup.string().required("Training type is required"),
    FromDate: Yup.string().required("From date is required"),
    ToDate: Yup.string()
      .required("To date is required")
      .test(
        "is-after-from",
        "To date must be after From date",
        function (value) {
          const { FromDate } = this.parent;
          if (!FromDate || !value) return true;
          return moment(value, "DD-MMM-YYYY").isSameOrAfter(
            moment(FromDate, "DD-MMM-YYYY")
          );
        }
      ),
    Remark: Yup.string().max(500, "Remark cannot exceed 500 characters"),
  });

  useEffect(() => {
    dispatch(updateNavbarHeading("Add Training Incentives"));
  }, []);

  useEffect(() => {
    fetchTrainingData();
  }, []);

  const fetchTrainingData = async () => {
    try {
      const response = await GetServiceCaller(
        `${API_URL}projectmanagement/training-incentive-getdata`
      );
      const projectList = response?.data?.data?.project;
      const brandList = projectList?.map((ele, index) => ({
        value: ele?.value,
        label: ele?.text,
        client: ele?.Client,
        clientID: ele?.ClientID,
      }));
      setProjectOption(brandList);

      const typeList = response?.data?.data?.trainingtype;
      const tyoeList = typeList?.map((ele, index) => ({
        value: ele?.value,
        label: ele?.text,
      }));
      setTypeOption(tyoeList);
      const datesData = response?.data?.data?.disabledate;
      setDisableDates(datesData);
    } catch (error) {
      console.error("Error fetching skills:", error);
    }
  };

  const calculateDays = (fromDate, toDate, disableDates, includeHolidays) => {
    if (!fromDate || !toDate) return 0;

    const start = moment(fromDate, "DD-MMM-YYYY");
    const end = moment(toDate, "DD-MMM-YYYY");
    const range = [];

    while (start <= end) {
      range.push(start.format("YYYY-MM-DD"));
      start.add(1, "day");
    }

    const disabledSet = new Set(
      disableDates.map((date) => moment(date).format("YYYY-MM-DD"))
    );

    return includeHolidays
      ? range.length
      : range.filter((dateStr) => !disabledSet.has(dateStr)).length;
  };

  const validateField = async (fieldName, value) => {
    try {
      await validationSchema.validateAt(fieldName, { [fieldName]: value });
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [fieldName]: err.message }));
    }
  };

  const handleSelect = async (selectedOption, name) => {
    const value = selectedOption?.value || "";

    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        [name]: value,
        Client: selectedOption?.client || "",
        ClientID: selectedOption?.clientID || "",
      };
      return updatedData;
    });

    validateField(name, value);
  };

  const handleChange = (e) => {
    const { name, type, value } = e.target;

    // Special handling for checkbox
    let val;
    if (type === "checkbox") {
      val = e.target.checked ? 1 : 0;
    } else {
      val = value;
    }

    setFormData((prevData) => {
      let updated = {
        ...prevData,
        [name]: val,
      };

      if (name === "AmountPerDay" || name === "Days") {
        updated.AmountPerDay = 1000;
      }

      if (updated.Days && updated.AmountPerDay) {
        updated.TotalAmount =
          Number(updated.Days) * Number(updated.AmountPerDay);
      }

      if (name === "IsIncludeHoliday" && updated.FromDate && updated.ToDate) {
        updated.Days = calculateDays(
          updated.FromDate,
          updated.ToDate,
          disableDates,
          val === 1 // Convert back to boolean for the calculateDays function
        );
        updated.TotalAmount = updated.Days * 1000;
      }

      return updated;
    });

    if (type !== "checkbox" && name !== "TrainigTitle") {
      validateField(name, val);
    }
  };

  const handleDateChange = (date, name) => {
    const dateString = date ? moment(date).format("DD-MMM-YYYY") : "";

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [name]: dateString,
      };

      if (newFormData.FromDate && newFormData.ToDate) {
        const days = calculateDays(
          newFormData.FromDate,
          newFormData.ToDate,
          disableDates,
          newFormData.IsIncludeHoliday
        );

        newFormData.Days = days;
        newFormData.AmountPerDay = 1000;
        newFormData.TotalAmount = days * 1000;
      }

      return newFormData;
    });

    validateField(name, dateString);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Validate the form data
      await validationSchema.validate(formData, { abortEarly: false });

      // Get the selected project to access client data
      const selectedProject = projectOption.find(
        (project) => project.value === formData.ProjectID
      );

      // Prepare the data for API submission
      const updatedFormData = {
        ...formData,
        AppliedPerDayAmount: formData.AmountPerDay,
        TotatAmountApplied: formData.TotalAmount,
        Client: selectedProject?.client || "", // Ensure Client is included
        ClientID: selectedProject?.clientID || "", // Ensure ClientID is included
      };

      console.log("Final Form Data to Submit:", updatedFormData);

      // Send the form data to the API
      const response = await PostFormServiceCaller(
        `${API_URL}projectmanagement/project-training`,
        updatedFormData,
        "post"
      );

      if (response.status === 200) {
        // If the API request is successful, show a success toast
        toast.success("Form submitted successfully!");
        setTimeout(() => {
          setLoading(false);
          navigate("/training-incentive");
        }, 1000);
      }
    } catch (err) {
      // Handle validation errors
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);

      // Scroll to the first error
      const firstError = err.inner[0];
      if (firstError) {
        const element = document.getElementById(firstError.path);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
    }
  };

  return (
    <>
      <div className="container-fluid" id="skill_section">
        <div className="container-fluid" id="skill-table-area">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-3">
                <label className="form-label">
                  Project<span className="text-danger">*</span>
                </label>
                <Select
                  name="ProjectID"
                  onChange={(selectedOption) =>
                    handleSelect(selectedOption, "ProjectID")
                  }
                  value={projectOption?.find(
                    (o) => o.value === formData?.ProjectID
                  )}
                  className={errors.ProjectID ? "is-invalid" : ""}
                  options={projectOption}
                />
                {errors.ProjectID && (
                  <div className="invalid-feedback d-block">
                    {errors.ProjectID}
                  </div>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label">Client</label>
                <input
                  type="text"
                  className="form-control"
                  id="Client"
                  name="Client"
                  value={formData.Client || ""}
                  onChange={(e) => handleChange(e)}
                  disabled
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-control"
                  id="TrainigTitle"
                  name="TrainigTitle"
                  value={formData.TrainigTitle}
                  onChange={(e) => handleChange(e)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  Type<span className="text-danger">*</span>
                </label>
                <Select
                  name="TrainingType"
                  onChange={(selectedOption) =>
                    handleSelect(selectedOption, "TrainingType")
                  }
                  value={typeOption?.find(
                    (o) => o.value === formData?.TrainingType
                  )}
                  className={errors.TrainingType ? "is-invalid" : ""}
                  options={typeOption}
                />
                {errors.TrainingType && (
                  <div className="invalid-feedback d-block">
                    {errors.TrainingType}
                  </div>
                )}
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-md-3">
                <label className="form-label">
                  From Date<span className="text-danger">*</span>
                </label>
                <ReactDatePicker
                  autoComplete="off"
                  changeYear
                  changeMonth
                  showPopperArrow
                  showIcon
                  icon="fa fa-calendar"
                  className={`form-control ${
                    errors.FromDate ? "is-invalid" : ""
                  }`}
                  format="d-MM-YYYY"
                  name="FromDate"
                  id="FromDate"
                  onChange={(date) => handleDateChange(date, "FromDate")}
                  selected={
                    formData.FromDate
                      ? moment(formData.FromDate, "DD-MMM-YYYY").toDate()
                      : null
                  }
                  dateFormat="dd-MMM-yyyy"
                  excludeDates={disableDates.map((date) => new Date(date))}
                  minDate={moment().subtract(1, "months").startOf("month").toDate()}
                  maxDate={moment().add(1, "months").endOf("month").toDate()}
                />
                {errors.FromDate && (
                  <div className="invalid-feedback d-block">
                    {errors.FromDate}
                  </div>
                )}
              </div>
              <div className="col-md-3">
                <label className="form-label">
                  To Date<span className="text-danger">*</span>
                </label>
                <ReactDatePicker
                  autoComplete="off"
                  changeYear
                  changeMonth
                  showPopperArrow
                  showIcon
                  icon="fa fa-calendar"
                  className={`form-control ${
                    errors.ToDate ? "is-invalid" : ""
                  }`}
                  format="d-MM-YYYY"
                  name="ToDate"
                  id="ToDate"
                  onChange={(date) => handleDateChange(date, "ToDate")}
                  selected={
                    formData.ToDate
                      ? moment(formData.ToDate, "DD-MMM-YYYY").toDate()
                      : null
                  }
                  dateFormat="dd-MMM-yyyy"
                  excludeDates={disableDates.map((date) => new Date(date))}
                  minDate={moment().subtract(1, "months").startOf("month").toDate()}
                  maxDate={moment().add(1, "months").endOf("month").toDate()}
                />
                {errors.ToDate && (
                  <div className="invalid-feedback d-block">
                    {errors.ToDate}
                  </div>
                )}
              </div>
              <div className="col-md-3 d-flex gap-2">
                <div className="col-md-6">
                  <label className="form-label">Days</label>
                  <input
                    type="number"
                    className="form-control"
                    id="Days"
                    name="Days"
                    value={formData.Days}
                    onChange={(e) => handleChange(e)}
                    disabled
                  />
                </div>
                <div className="col-md-6 mt-2 pt-4">
                  <label
                    htmlFor="flexSwitchCheckChecked"
                    className="form-label me-2"
                  >
                    Include Holiday
                  </label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="IsIncludeHoliday"
                    id="flexSwitchCheckChecked"
                    checked={formData.IsIncludeHoliday === 1} // Check if value is 1
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="d-flex col-md-3">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">
                      Amount (INR)<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control text-end"
                      id="AmountPerDay"
                      name="AmountPerDay"
                      value={1000}
                      disabled
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Total</label>
                    <input
                      type="text"
                      className="form-control text-end"
                      id="TotalAmount"
                      name="TotalAmount"
                      value={formData.TotalAmount}
                      onChange={(e) => handleChange(e)}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-md-3">
                <label className="form-label">Remark</label>
                <textarea
                  type="text"
                  className={`form-control ${
                    errors.Remark ? "is-invalid" : ""
                  }`}
                  id="Remark"
                  name="Remark"
                  value={formData.Remark}
                  onChange={(e) => handleChange(e)}
                />
                {errors.Remark && (
                  <div className="invalid-feedback d-block">
                    {errors.Remark}
                  </div>
                )}
              </div>
            </div>
            <div className="text-end mt-3">
              <button type="button" className="cancel-btn me-2"   onClick={() => navigate('/training-incentive')}>
                Cancel
              </button>
              <button type="submit" className="custom-btn" disabled={loading}>
                {loading ? (
                  <FaSpinner className="animate-spin mr-2 inline" />
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddTrainingIncentive;
